/**
 * Header component with navigation
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusSquare, Menu, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { PhotoUploadForm } from '../photos/PhotoUploadForm';

export function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { theme } = useTheme();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getInitials = (displayName: string) => {
    return displayName
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img
            src={theme === 'dark' ? '/logo-white.png' : '/logo-black.png'}
            alt="Muenstergram"
            className="h-12"
          />
        </Link>

        {/* Desktop Navigation - hidden on mobile */}
        {isAuthenticated && user && (
          <nav className="hidden items-center space-x-4 md:flex">
            <Link
              to="/family"
              className="text-sm font-medium hover:underline"
              data-testid="family-members-link"
            >
              Family
            </Link>

            {user.isAdmin && (
              <Link
                to="/admin/invites"
                className="text-sm font-medium hover:underline"
                data-testid="admin-invites-link"
              >
                Invites
              </Link>
            )}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setUploadModalOpen(true)}
              title="Upload photo"
            >
              <PlusSquare className="h-5 w-5" />
            </Button>

            <Link to={`/profile/${user.userId}`} className="flex items-center space-x-2">
              <Avatar className="h-8 w-8">
                {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.displayName} />}
                <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{user.displayName}</span>
            </Link>

            <Button variant="ghost" size="sm" onClick={() => logout()}>
              Logout
            </Button>
          </nav>
        )}

        {/* Mobile Navigation - shown only on mobile */}
        {isAuthenticated && user && (
          <div className="flex items-center space-x-2 md:hidden">
            {/* Upload photo button for mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setUploadModalOpen(true)}
              title="Upload photo"
            >
              <PlusSquare className="h-5 w-5" />
            </Button>

            {/* Hamburger menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        )}
      </div>

      {/* Mobile menu dropdown */}
      {isAuthenticated && user && mobileMenuOpen && (
        <div className="border-t bg-background md:hidden">
          <nav className="container flex flex-col space-y-3 py-4">
            <Link
              to="/family"
              className="py-2 text-sm font-medium hover:underline"
              onClick={() => setMobileMenuOpen(false)}
            >
              Family
            </Link>

            {user.isAdmin && (
              <Link
                to="/admin/invites"
                className="py-2 text-sm font-medium hover:underline"
                onClick={() => setMobileMenuOpen(false)}
              >
                Invites
              </Link>
            )}

            <Link
              to={`/profile/${user.userId}`}
              className="flex items-center space-x-2 py-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Avatar className="h-8 w-8">
                {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.displayName} />}
                <AvatarFallback>{getInitials(user.displayName)}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{user.displayName}</span>
            </Link>

            <Button
              variant="ghost"
              className="justify-start py-2"
              onClick={() => {
                logout();
                setMobileMenuOpen(false);
              }}
            >
              Logout
            </Button>
          </nav>
        </div>
      )}

      {/* Upload modal */}
      <PhotoUploadForm open={uploadModalOpen} onOpenChange={setUploadModalOpen} />
    </header>
  );
}

export default Header;
